import {QueryCacheKeys} from '../../api/cache';
import {getProjects} from '../../api/projects';
import {addProjectsToTransactions, removeProjectsFromTransactions} from '../../api/transactions';
import {stopPropagation} from '../../util/function';
import ProjectPicker from './ProjectPicker';
import {Icon, Popout, Tooltip} from '@sproutsocial/racine';
import {MOTION_DURATION_MEDIUM} from '@sproutsocial/seeds-motion/dist/seeds-motion-unitless';
import {useQuery} from '@tanstack/react-query';
import {SyntheticEvent, useCallback, useState} from 'react';
import {keyBy} from 'shared/src/map';
import type {Transaction} from 'shared/src/models';

interface TransactionProjectPickerProps {
	transactions: Transaction[];
	/**
	 * True for read-only mode
	 */
	disabled?: boolean;
	onChange?: () => void;
}

const TransactionProjectPicker = ({
	transactions,
	disabled,
	onChange,
}: TransactionProjectPickerProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const onClick = useCallback(
		(event: SyntheticEvent) => {
			event.stopPropagation();

			setIsOpen(disabled ? false : !isOpen);
		},
		[isOpen, disabled],
	);

	const {data: projects} = useQuery({
		queryKey: QueryCacheKeys.projects(),
		queryFn: () => getProjects(),
		select: (projects) => keyBy(projects, (project) => project.id),
	});

	const selectedProjectIds =
		transactions.length === 0
			? new Set<string>()
			: transactions
					.map((transaction) => new Set<string>(transaction.projects))
					.reduce((a, b) => a.intersection(b));

	const selectedProjectNames = Array.from(
		new Set(
			transactions.flatMap((transaction) =>
				transaction.projects?.map((projectId) => projects?.get(projectId)?.name),
			),
		),
	).join(', ');

	const indeterminateProjectIds =
		transactions.length <= 1
			? new Set<string>()
			: new Set(transactions.flatMap((transaction) => transaction.projects)).difference(
					selectedProjectIds,
				);

	const onProjectSelected = useCallback(
		async (projectId: string) => {
			await addProjectsToTransactions(
				transactions.map((transaction) => transaction.id),
				[projectId],
			);

			onChange?.();
		},
		[transactions, onChange],
	);

	const onProjectDeselected = useCallback(
		async (projectId: string) => {
			await removeProjectsFromTransactions(
				transactions.map((transaction) => transaction.id),
				[projectId],
			);

			onChange?.();
		},
		[transactions, onChange],
	);

	return (
		<Popout
			isOpen={isOpen}
			setIsOpen={setIsOpen}
			content={
				<Popout.Content onClick={stopPropagation}>
					<ProjectPicker
						selectedProjectIds={selectedProjectIds}
						indeterminateProjectIds={indeterminateProjectIds}
						onProjectSelected={onProjectSelected}
						onProjectDeselected={onProjectDeselected}
					/>
				</Popout.Content>
			}
		>
			<Tooltip
				content={transactions.length === 1 ? selectedProjectNames : undefined}
				enterDelay={MOTION_DURATION_MEDIUM * 1000}
			>
				<Icon
					cursor={disabled ? undefined : 'pointer'}
					name={
						selectedProjectIds.size > 0 || indeterminateProjectIds.size > 0
							? 'screwdriver-hammer-solid'
							: 'screwdriver-hammer-outline'
					}
					onClick={onClick}
				/>
			</Tooltip>
		</Popout>
	);
};

export default TransactionProjectPicker;
